import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { tagsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Tag } from '../types';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  error?: string;
}

const TagInput: React.FC<TagInputProps> = ({ value = [], onChange, placeholder, error }) => {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAllTags();
  }, []);

  const fetchAllTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setInputValue(input);

    if (input.trim()) {
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(input.toLowerCase()) &&
        !value.includes(tag.name)
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      // Show all unused tags when input is focused but empty
      const unused = allTags.filter(tag => !value.includes(tag.name));
      setSuggestions(unused.slice(0, 10));
      setShowSuggestions(true);
    }
  };

  const addTag = async (tagName: string) => {
    const trimmed = tagName.trim();
    if (!trimmed || value.includes(trimmed)) return;

    // If the tag doesn't exist in the managed list, create it as a personal tag
    const exists = allTags.some(t => t.name.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      try {
        const response = await tagsAPI.create({ name: trimmed, type: 'user' });
        setAllTags(prev => [...prev, response.data]);
      } catch (err) {
        // Tag might already exist server-side, continue applying it
        console.error('Failed to create tag:', err);
      }
    }

    onChange([...value, trimmed]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const handleSuggestionClick = (tag: Tag) => {
    addTag(tag.name);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    const unused = allTags.filter(tag => !value.includes(tag.name));
    setSuggestions(inputValue.trim()
      ? unused.filter(t => t.name.toLowerCase().includes(inputValue.toLowerCase()))
      : unused.slice(0, 10)
    );
    setShowSuggestions(true);
  };

  // Find the tag object for a given name (for color display)
  const getTagColor = (name: string): string | undefined => {
    return allTags.find(t => t.name === name)?.color || undefined;
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Tags
      </label>
      <div className={`relative px-2 py-1.5 border ${error ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus-within:ring-1 focus-within:ring-primary focus-within:border-primary bg-white`}>
        <div className="flex flex-wrap gap-1.5 items-center">
          {value.map((tag, index) => {
            const color = getTagColor(tag);
            return (
              <span
                key={index}
                className="text-white"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '0 8px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  lineHeight: '22px',
                  fontWeight: 500,
                  backgroundColor: color || 'rgb(var(--color-primary))'
                }}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="opacity-70 hover:opacity-100 transition-opacity flex items-center"
                  style={{ padding: 0, lineHeight: 0 }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={value.length === 0 ? placeholder || 'Add tags...' : ''}
            className="flex-1 min-w-[120px] border-none outline-none focus:ring-0 p-0 text-sm"
          />
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Admin tags */}
            {suggestions.some(t => t.type === 'admin') && (
              <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase bg-gray-50">
                Admin Tags
              </div>
            )}
            {suggestions.filter(t => t.type === 'admin').map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleSuggestionClick(tag)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color || '#9CA3AF' }}
                />
                {tag.name}
              </button>
            ))}
            {/* User tags */}
            {suggestions.some(t => t.type === 'user') && (
              <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase bg-gray-50">
                Personal Tags
              </div>
            )}
            {suggestions.filter(t => t.type === 'user').map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleSuggestionClick(tag)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color || '#9CA3AF' }}
                />
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      <p className="mt-1 text-xs text-gray-500">
        Press Enter or comma to add a tag
      </p>
    </div>
  );
};

export default TagInput;
